import { CompatibilityReason } from "./compatibility";
import { UserFacingError } from "../store/types";

export type { UserFacingError };

const FALLBACK: UserFacingError = {
  title: "Etwas ist schiefgelaufen",
  body:
    "Der Sprachassistent ist gerade nicht verfügbar. Bitte laden Sie die Seite neu oder versuchen Sie es später erneut.",
  severity: "error",
};

function runtimeErrorMessage(raw: unknown): string {
  if (typeof raw === "string") return raw;

  const maybeError = raw as { message?: unknown };
  if (typeof maybeError?.message === "string") return maybeError.message;

  const maybeCloseEvent = raw as {
    code?: unknown;
    reason?: unknown;
    wasClean?: unknown;
    type?: unknown;
    constructor?: { name?: string };
  };
  const isCloseEventLike =
    maybeCloseEvent?.constructor?.name === "CloseEvent" ||
    (maybeCloseEvent?.type === "close" && typeof maybeCloseEvent?.code === "number");

  if (isCloseEventLike) {
    const reason =
      typeof maybeCloseEvent.reason === "string" && maybeCloseEvent.reason.length > 0
        ? maybeCloseEvent.reason
        : "empty";
    return `CloseEvent code=${String(maybeCloseEvent.code)} reason=${reason} wasClean=${String(maybeCloseEvent.wasClean)}`;
  }

  return String(raw || "");
}

export function compatibilityError(reason: CompatibilityReason): UserFacingError {
  switch (reason) {
    case "in-app-browser":
      return {
        title: "In diesem Browser nicht unterstützt",
        body:
          "Sprachfunktionen sind in In-App-Browsern (z. B. Facebook, Instagram) nicht verfügbar. Bitte öffnen Sie die Seite in Safari, Chrome oder Firefox.",
        severity: "warning",
      };
    case "insecure-context":
      return {
        title: "Sichere Verbindung erforderlich",
        body:
          "Der Sprachassistent benötigt eine HTTPS-Verbindung. Bitte öffnen Sie die Seite über https://.",
        severity: "error",
      };
    case "mic-denied":
      return {
        title: "Mikrofonzugriff blockiert",
        body:
          "Bitte erlauben Sie den Mikrofonzugriff für diese Seite in den Browser-Einstellungen und laden Sie die Seite neu.",
        severity: "warning",
      };
    case "no-getusermedia":
      return {
        title: "Browser nicht kompatibel",
        body:
          "Ihr Browser unterstützt keine Sprachaufnahme. Bitte verwenden Sie eine aktuelle Version von Chrome, Safari, Firefox oder Edge.",
        severity: "error",
      };
    default:
      return FALLBACK;
  }
}

export function translateRuntimeError(raw: unknown): UserFacingError {
  const msg = runtimeErrorMessage(raw);
  const lower = msg.toLowerCase();

  const isMicDenied =
    lower.includes("notallowederror") ||
    lower.includes("permission denied") ||
    (lower.includes("microphone") && lower.includes("denied"));
  if (isMicDenied) {
    return compatibilityError("mic-denied");
  }
  if (
    lower.includes("notfounderror") ||
    lower.includes("no microphone") ||
    lower.includes("device not found")
  ) {
    return {
      title: "Kein Mikrofon gefunden",
      body:
        "Es wurde kein Mikrofon erkannt. Bitte schließen Sie ein Mikrofon an oder prüfen Sie die Systemeinstellungen.",
      severity: "error",
    };
  }
  if (lower.includes("notsupportederror") || lower.includes("not supported")) {
    return compatibilityError("no-getusermedia");
  }
  // Reproduced in QA on 2026-04-28: ElevenLabs SDK throws this when the
  // captured MediaStream is not a real browser MediaStream (e.g. virtual mic
  // device, faked stream, or a stream that gets stopped before the audio
  // graph is wired up). The user-facing surface should explain the audio
  // pipeline rather than the generic "service unavailable" fallback.
  if (
    lower.includes("createmediastreamsource") ||
    lower.includes("not of type 'mediastream'") ||
    lower.includes("audiocontext")
  ) {
    return {
      title: "Mikrofon konnte nicht initialisiert werden",
      body:
        "Der Audiokanal konnte nicht aufgebaut werden. Bitte stellen Sie sicher, dass ein gewöhnliches Mikrofon ausgewählt ist (kein virtuelles Audiogerät), und laden Sie die Seite neu.",
      severity: "error",
    };
  }
  if (
    lower.includes("failed to get signed url") ||
    lower.includes("invalid signed url") ||
    lower.includes("failed to fetch conversation token") ||
    lower.includes("conversation token")
  ) {
    return {
      title: "Verbindung fehlgeschlagen",
      body:
        "Der Sprachassistent konnte keine Verbindung zum Server aufbauen. Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.",
      severity: "error",
    };
  }
  if (
    lower.includes("closeevent") ||
    lower.includes("code=1006") ||
    lower.includes("socket error") ||
    lower.includes("closed by the server")
  ) {
    return {
      title: "Echtzeitverbindung blockiert",
      body:
        "Der Browser konnte die sichere Echtzeitverbindung zum Sprachdienst nicht öffnen. Bitte prüfen Sie VPN, Werbeblocker und Browser-Schutz. In Firefox schlägt das oft fehl — bitte Chrome oder Edge versuchen, oder Tracking-Schutz für diese Seite lockern.",
      severity: "warning",
    };
  }
  if (lower.includes("quota")) {
    return {
      title: "Tägliches Limit erreicht",
      body:
        "Das tägliche Gesprächslimit wurde erreicht. Bitte versuchen Sie es morgen wieder.",
      severity: "info",
    };
  }
  if (lower.includes("websocket") || lower.includes("network")) {
    return {
      title: "Netzwerkproblem",
      body:
        "Die Verbindung zum Sprachassistenten wurde unterbrochen. Bitte prüfen Sie Ihre Internetverbindung.",
      severity: "warning",
    };
  }
  // No matched pattern — keep the generic German fallback, but expose the raw
  // string in the console so future tester reports include real evidence
  // instead of leaving us to guess from a translated popup.
  console.error("[Widget:errorMessages] unmapped runtime error:", msg);
  return FALLBACK;
}

export function serializeUserError(err: UserFacingError): string {
  return `${err.title}: ${err.body}`;
}
